// =====================================================================
// src/modulos/reserva_de_vaga.js — "Nova Reserva de Vaga" (0097, 6A)
//
// Um modal, três portas (Hugo, 01/11/2026, 2A): o botão da Garagem, a
// célula livre do quadro da Garagem e o atalho da ficha do check-in. E uma
// quarta (3B): "Pedir vaga" nos Detalhes da Reserva, para a imobiliária.
//
// A marcação é a do MdM — `modalNovaGaragemReserva` (index.html 8702) e
// `garagemForm` (logo abaixo): título, "Vincular a uma reserva", as duas
// datas lado a lado, os botões Cancelar | Salvar com flex 1. O que ficou
// de fora, e por quê:
//   · valor da diária e total, e o aviso "100% Monteiro de Moraes": o
//     hotel nunca vê valor, e a vaga aqui é do proprietário, sem preço;
//   · "Entrada livre (sem vínculo)" e o "Flat associado" avulso: a vaga
//     daqui sempre é de uma reserva (0032), e é ela que dá o período;
//   · o "Salvar mesmo assim?" do conflito: aqui vaga ocupada é NUNCA
//     (Hugo, 18/09/2026). O seletor só oferece as livres do período
//     (`vagas_livres`), e o banco recusa de novo se alguém foi mais rápido.
// =====================================================================
import { el, abrirModal, fecharModal, aviso } from '../ui.js';
import { periodoDaVagaNaReserva, rotuloDaReservaParaVaga, normalizarPlaca } from '../lib/garagem.js';

// reserva: a reserva fixa ({ id, quarto, hospede, check_in, check_out }),
//          ou nulo para escolher entre `opcoes` (reservas_para_vaga)
// de, vagaId: o dia e a vaga da célula clicada no quadro
export function modalReservaDeVaga({ auth, reserva = null, opcoes = [], de = null, vagaId = null, aoSalvar = () => {} }) {
  let atual = reserva || null;
  let pedido = 0;   // a última consulta de vagas é a que vale

  const erro = el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' });
  const mostrarErro = m => { erro.textContent = m || ''; erro.classList.toggle('hidden', !m); };
  const inDe = el('input', { class: 'input', id: 'rv-vaga-de', type: 'date', onchange: () => carregarVagas() });
  const inAte = el('input', { class: 'input', id: 'rv-vaga-ate', type: 'date', onchange: () => carregarVagas() });
  const selVaga = el('select', { class: 'input', id: 'rv-vaga' });
  // a placa normaliza enquanto se digita, como em "+ Declarar placa" (garagem.js)
  const inPlaca = el('input', { class: 'input', id: 'rv-vaga-placa', placeholder: 'ABC1D23', maxlength: '8', autocomplete: 'off', style: { fontWeight: '700', letterSpacing: '1px' },
    oninput: ev => { ev.target.value = normalizarPlaca(ev.target.value); } });

  function preencherDatas() {
    if (!atual) { inDe.value = ''; inAte.value = ''; return; }
    inDe.value = de && de >= atual.check_in && de <= atual.check_out ? de : atual.check_in;
    inAte.value = atual.check_out;
  }

  async function carregarVagas() {
    const meu = ++pedido;
    if (!atual) { selVaga.replaceChildren(el('option', { value: '' }, 'Escolha a reserva primeiro')); return; }
    const e = periodoDaVagaNaReserva(inDe.value, inAte.value, atual);
    if (e) { mostrarErro(e); selVaga.replaceChildren(el('option', { value: '' }, '—')); return; }
    mostrarErro('');
    selVaga.replaceChildren(el('option', { value: '' }, 'Carregando…'));
    let livres;
    try { livres = (await auth.rpc('vagas_livres', { p_reserva: atual.id, p_de: inDe.value, p_ate: inAte.value })) || []; }
    catch (x) { if (meu === pedido) { mostrarErro(x.message); selVaga.replaceChildren(el('option', { value: '' }, '—')); } return; }
    if (meu !== pedido) return;
    selVaga.replaceChildren(...(livres.length
      ? livres.map(v => el('option', { value: v.id, selected: v.id === vagaId }, v.identificacao + (v.setor ? ` · ${v.setor}` : '')))
      : [el('option', { value: '' }, 'Nenhuma vaga livre no período')]));
    if (vagaId && !livres.some(v => v.id === vagaId)) mostrarErro('A vaga escolhida no quadro não está livre em todo o período.');
  }

  const campoReserva = reserva
    ? el('div', { class: 'input', id: 'rv-vaga-reserva-fixa', style: { background: '#f9fafb' } }, rotuloDaReservaParaVaga(reserva))
    : el('select', { class: 'input', id: 'rv-vaga-reserva', onchange: ev => {
        atual = opcoes.find(o => o.reserva_id === ev.target.value || o.id === ev.target.value) || null;
        if (atual && !atual.id) atual = { ...atual, id: atual.reserva_id };
        preencherDatas(); carregarVagas();
      } },
      el('option', { value: '' }, opcoes.length ? '— Escolha a reserva —' : 'Nenhuma reserva confirmada no período'),
      opcoes.map(o => el('option', { value: o.reserva_id || o.id }, rotuloDaReservaParaVaga(o))));

  const form = el('form', { id: 'form-reserva-vaga', onsubmit: async ev => {
    ev.preventDefault();
    if (!atual) { mostrarErro('Escolha a reserva'); return; }
    const e = periodoDaVagaNaReserva(inDe.value, inAte.value, atual);
    if (e) { mostrarErro(e); return; }
    if (!selVaga.value) { mostrarErro('Escolha a vaga'); return; }
    try {
      await auth.rpc('reservar_vaga', { p_vaga: selVaga.value, p_reserva: atual.id, p_de: inDe.value, p_ate: inAte.value, p_placa: inPlaca.value.trim() || null });
    } catch (x) { mostrarErro(x.message); carregarVagas(); return; }
    fecharModal();
    aviso(`Vaga reservada · quarto ${atual.quarto}.`);
    aoSalvar();
  } },
    el('h2', { class: 'modal-title' }, 'Nova Reserva de Vaga'),
    erro,
    el('div', { class: 'form-row' }, el('label', { class: 'label', for: reserva ? null : 'rv-vaga-reserva' }, 'Reserva do apartamento'), campoReserva,
      el('div', { style: { fontSize: '11px', color: '#6b7280', marginTop: '4px' } }, 'O período vem da reserva; pode encurtar.')),
    el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' } },
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'rv-vaga-de' }, 'De'), inDe),
      el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'rv-vaga-ate' }, 'Até'), inAte)),
    el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'rv-vaga' }, 'Vaga'), selVaga),
    el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'rv-vaga-placa' }, 'Placa (opcional agora — confirma no check-in)'), inPlaca),
    el('div', { style: { display: 'flex', gap: '8px', marginTop: '20px' } },
      el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Cancelar'),
      el('button', { type: 'submit', class: 'btn btn-primary', id: 'btn-rv-vaga-salvar', style: { flex: '1' } }, 'Salvar')));

  abrirModal(form);
  preencherDatas();
  carregarVagas();
}
